"use client";

import * as React from "react";
import { Tooltip as TooltipPrimitive } from "radix-ui";

import { cn } from "../../lib/utils";

/* The Sirdab tooltip: a hint, never a home for anything essential. Ink
   chip: primary fill, primary-foreground caption text, no arrow.
   [interim] The geometry is derived from the system (caption role, md
   radius, shadow-popover-sm).

   A lone Tooltip works with zero setup (it wraps its own 300ms
   provider). For CLUSTERS (a toolbar of icon buttons), wrap the region
   in ONE <TooltipProvider>: the tooltips inside defer to it, so moving
   between adjacent triggers reopens instantly (skipDelayDuration)
   instead of re-waiting 300ms each time.

   Usage:
       <Tooltip>
           <TooltipTrigger asChild><Button …/></TooltipTrigger>
           <TooltipContent>انسخ الرابط</TooltipContent>
       </Tooltip>

       <TooltipProvider>            // one per toolbar/cluster
           <Tooltip>…</Tooltip>
           <Tooltip>…</Tooltip>
       </TooltipProvider>
*/
/* Radix requires SOME Provider above every Root. Tooltip self-wraps when
   standalone, but inside our TooltipProvider it must NOT (a nested
   provider would isolate the tooltip from the cluster's skip-delay).
   Radix has no "am I inside a provider" hook, so this flag is ours. */
const HasProviderContext = React.createContext(false);

function TooltipProvider({
  delayDuration = 300,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration} {...props}>
      <HasProviderContext.Provider value={true}>
        {children}
      </HasProviderContext.Provider>
    </TooltipPrimitive.Provider>
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const hasProvider = React.useContext(HasProviderContext);
  const root = <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
  if (hasProvider) return root;
  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      {root}
    </TooltipPrimitive.Provider>
  );
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          // Both open states animate: delayed-open (normal) AND
          // instant-open (skip-delay inside a TooltipProvider
          // cluster). Keying off one of them left the other
          // popping in unanimated.
          "z-tooltip bg-primary text-caption text-primary-foreground shadow-popover-sm data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-[state=instant-open]:animate-in data-[state=instant-open]:fade-in-0 data-[state=instant-open]:zoom-in-95 rounded-md px-2 py-1 duration-100",
          className,
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
